import { List, Map, Set } from "immutable";

import { RunnableQuery, topologicallySortQueries } from "../RunnableQuery";
import type { Tuple } from "../Tuple";
import { deleteChange } from "../Tuples";
import { insert } from "../Tuples/Change";
import { ZSet } from "../ZSet";
import { type JsonPathLiteral } from "../jsonTable/JsonPathLiteral";
import { pluck } from "../jsonTable/pluck";
import { type TopLevelQuerySpec, compoundQueryKey, contentId } from "../operatorsV2/QuerySpec";
import { type SqliteColumnDef } from "../schema";
import { makeReactionTableName } from "../sqlite/makeReactionTableName";
import { type V1ViewColumnDef as ViewColumnDef } from "../v1-base-spec";
import {
	type Action,
	type ColumnAppendAction,
	type ColumnDeleteAction,
	type ColumnRenameAction,
	type ColumnTypeChangeAction,
} from "./Action";
import {
	type ActionIntent,
	type ColumnAppendActionIntent,
	type ColumnDeleteActionIntent,
	type ColumnRenameActionIntent,
	type ColumnTypeChangeActionIntent,
	type MutationActionIntent,
	isColumnAppendActionIntent,
	isColumnDeleteActionIntent,
	isColumnRenameActionIntent,
	isColumnTypeChangeActionIntent,
	isMutationActionIntent,
} from "./ActionIntent";
import { type QueryEvaluatorState, emptyQueryEvaluatorState } from "./QueryManagerState";
import { type TableMutation } from "./TableMutation";
import { reconciliationPlan } from "./reconciliationPlan";

export * from "./Action";
export * from "./TableMutation";

export class QueryEvaluator {
	private _committedState: QueryEvaluatorState;
	private _actionIntents: List<ActionIntent> = List();
	private _topoSortedQueries: RunnableQuery[];

	constructor(
		tables: { [key: string]: unknown },
		private _maintainEphemeralLog = true
	) {
		const initialState = emptyQueryEvaluatorState();
		for (const tableId of Object.keys(tables)) {
			initialState.tables = initialState.tables.set(tableId, ZSet.empty());
		}
		this._committedState = initialState;
		this._topoSortedQueries = topologicallySortQueries(initialState.querySpecs);
	}

	get latestCommittedState(): QueryEvaluatorState {
		return this._committedState;
	}

	get latestState(): QueryEvaluatorState {
		return this._actionIntents.last()?.proposedState ?? this._committedState;
	}

	get latestEpoch(): number {
		return this.latestState.currentEpoch;
	}

	get pendingActions(): List<ActionIntent> {
		return this._actionIntents;
	}

	queryIntent(query: TopLevelQuerySpec, intentId: string = crypto.randomUUID()): ActionIntent {
		const state = this.latestState;

		let newQuerySpecs = state.querySpecs;
		let newQueryResults = state.queryResults;
		let newQueryStates = state.queryStates;

		const permissionSpec = query.permissionFilter ?? { type: "read_all_rows" };
		const topLevelContentId = contentId(query.spec);
		const specsToProcess = [...query.subquerySpecs, query.spec];

		for (const subquerySpec of specsToProcess) {
			const subqueryContentId = contentId(subquerySpec);
			const subqueryKey = compoundQueryKey({ contentId: subqueryContentId, permissionSpec });

			// We do not need to evaluate the query again if it has already run.
			if (newQueryResults.has(subqueryKey) && newQueryStates.has(subqueryKey)) {
				continue;
			}

			const newQuery =
				topLevelContentId === subqueryContentId
					? RunnableQuery.fromSpec(subquerySpec, permissionSpec, query.finalizer)
					: RunnableQuery.fromSpec(subquerySpec, permissionSpec);

			newQuerySpecs = newQuerySpecs.set(subqueryKey, newQuery);

			const { result, newStates } = newQuery.update(
				this.latestState.tables,
				newQueryResults,
				newQueryStates.get(subqueryKey)
			);

			newQueryResults = newQueryResults.set(subqueryKey, result);
			newQueryStates = newQueryStates.set(subqueryKey, newStates);
		}
		const newTopoSortedQueries = topologicallySortQueries(newQuerySpecs);

		// Create new state with updated specs and sorted queries
		const action: Action = {
			type: "queryRegistration",
			intentId,
			desiredStartEpoch: state.currentEpoch,
			querySpec: query,
		};
		const newState: QueryEvaluatorState = {
			currentEpoch: state.currentEpoch + 1,
			tables: state.tables,
			querySpecs: newQuerySpecs,
			queryResults: newQueryResults,
			queryStates: newQueryStates,
			actionLog: state.actionLog.push(action),
			error: state.error,
		};
		const actionIntent: ActionIntent = { action, proposedState: newState };

		if (this._maintainEphemeralLog) {
			this._topoSortedQueries = newTopoSortedQueries;
			this._actionIntents = this._actionIntents.push(actionIntent);
		}

		return actionIntent;
	}

	mutationIntent(
		tableId: string,
		desiredApplyEpoch: number,
		mutation: TableMutation<unknown>,
		intentId: string = crypto.randomUUID()
	): MutationActionIntent {
		const currentEpoch = this.latestEpoch;
		if (desiredApplyEpoch !== currentEpoch) {
			throw new Error(`Epoch mismatch: desired=${desiredApplyEpoch} current=${currentEpoch}`);
		}

		// Apply operations to the table
		const table = this.latestState.tables.get(tableId)?.clone() ?? ZSet.empty();

		if (mutation.type === "INSERT" && table?.hasId(mutation.record.id)) {
			throw new Error(`Record ${mutation.record.id} already exists`);
		} else if (mutation.type === "UPDATE" && !table?.hasId(mutation.recordId)) {
			throw new Error(`Record ${mutation.recordId} does not exist`);
		} else if (mutation.type === "DELETE" && !table?.hasId(mutation.recordId)) {
			throw new Error(`Record ${mutation.recordId} does not exist`);
		}

		const delta = table.deltaFromMutations(mutation);
		const currTables = this.latestState.tables.set(tableId, table.merge(delta));
		let currQueryDelta = Map<string, ZSet<unknown>>();
		let currQueryStates = this.latestState.queryStates;

		let updatedQueryIds = Set<string>();
		let changesByQueryId = Map<string, List<TableMutation<unknown>>>();
		const unchangedTables = currTables
			.keySeq()
			.map<[string, ZSet<unknown>]>((key) => [key, ZSet.empty<unknown>()]);

		for (const query of this._topoSortedQueries) {
			const { result, newStates } = query.update(
				Map([...unchangedTables, [tableId, delta]]),
				currQueryDelta,
				currQueryStates.get(query.queryKey) ?? Map()
			);
			currQueryDelta = currQueryDelta.set(query.queryKey, result);
			currQueryStates = currQueryStates.set(query.queryKey, newStates);
			if (result.size > 0) {
				updatedQueryIds = updatedQueryIds.add(query.queryKey);
			}

			let changes = List<TableMutation<unknown>>();
			for (const { tuple, weight } of result) {
				if (weight > 0) {
					changes = changes.push(insert(tuple));
				} else if (weight < 0) {
					changes = changes.push(deleteChange(tuple.id));
				}
			}
			changesByQueryId = changesByQueryId.set(query.queryKey, changes);
		}

		let queryResults = this.latestState.queryResults;

		for (const [queryId, delta] of currQueryDelta) {
			const currentResults = queryResults.get(queryId) ?? ZSet.empty();
			queryResults = queryResults.set(queryId, currentResults.merge(delta));
		}

		// Update the state
		const action: Action = {
			type: "mutation",
			intentId,
			table: tableId,
			desiredApplyEpoch,
			mutation,
		};
		const proposedStateUpdate: ActionIntent = {
			action,
			proposedState: {
				currentEpoch: desiredApplyEpoch + 1,
				tables: currTables,
				querySpecs: this.latestState.querySpecs,
				queryResults,
				queryStates: currQueryStates,
				actionLog: this.latestState.actionLog.push(action),
				error: this.latestState.error,
			},
			changesByQueryId,
		};

		if (this._maintainEphemeralLog) {
			this._actionIntents = this._actionIntents.push(proposedStateUpdate);
		}
		return proposedStateUpdate;
	}

	columnAppendIntent(
		tableId: string,
		sqliteColumnDef: SqliteColumnDef,
		viewColumnDef: ViewColumnDef,
		intentId: string = crypto.randomUUID()
	): ColumnAppendActionIntent {
		//
		// Get table.
		//

		const table = this.latestState.tables.get(tableId);
		if (!table) {
			throw new Error(`Table ${tableId} not found`);
		}

		//
		// Remove and re-add every row with a `NULL` value for the new column.
		//

		let delta = ZSet.empty<unknown>();
		for (const row of table) {
			// Remove current version of the row.
			delta = table.add({ ...row, weight: -1 });

			// Add new version of the row.
			const accessorKey: JsonPathLiteral = `$.${sqliteColumnDef.name}`;
			const newTuple = row.tuple.addOrReplace(accessorKey, null);
			delta = table.add({ ...row, weight: 1, tuple: newTuple });
		}

		//
		// Update the query results.
		//

		const { queryResults, queryStates, changesByQueryId } = this.updateQueryResults(
			tableId,
			delta
		);

		//
		// Construct the return value.
		//

		const action: ColumnAppendAction = {
			type: "columnAppend",
			intentId,
			table: tableId,
			desiredApplyEpoch: this.latestEpoch,
			sqliteColumnDef,
			viewColumnDef,
		};

		const proposedState: QueryEvaluatorState = {
			currentEpoch: this.latestEpoch + 1,
			tables: this.latestState.tables.set(tableId, table.merge(delta)),
			querySpecs: this.latestState.querySpecs,
			queryResults,
			queryStates,
			actionLog: this.latestState.actionLog.push(action),
			error: this.latestState.error,
		};

		return { action, proposedState, changesByQueryId };
	}

	columnDeleteIntent(
		tableId: string,
		columnName: string,
		intentId: string = crypto.randomUUID()
	): ColumnDeleteActionIntent {
		//
		// Get table.
		//

		const table = this.latestState.tables.get(tableId);
		if (!table) {
			throw new Error(`Table ${tableId} not found`);
		}

		//
		// Remove and re-add every row without the specified column.
		//

		let delta = ZSet.empty<unknown>();
		for (const row of table) {
			let newTuple: Tuple<unknown>;
			try {
				const path = `/${columnName}`;
				newTuple = row.tuple.applyPatch([{ op: "remove", path }]);
			} catch {
				// Skip the record if there is no column to remove.
				continue;
			}

			// Remove current version of the row.
			delta = table.add({ ...row, weight: -1 });

			// Add new version of the row without the specified column.
			delta = table.add({ ...row, weight: 1, tuple: newTuple });
		}

		//
		// Update the query results.
		//

		const { queryResults, queryStates, changesByQueryId } = this.updateQueryResults(
			tableId,
			delta
		);

		//
		// Construct the return value.
		//

		const action: ColumnDeleteAction = {
			type: "columnDelete",
			intentId,
			table: tableId,
			desiredApplyEpoch: this.latestEpoch,
			columnName,
		};

		const proposedState: QueryEvaluatorState = {
			currentEpoch: this.latestEpoch + 1,
			tables: this.latestState.tables.set(tableId, table.merge(delta)),
			querySpecs: this.latestState.querySpecs,
			queryResults,
			queryStates,
			actionLog: this.latestState.actionLog.push(action),
			error: this.latestState.error,
		};

		return { action, proposedState, changesByQueryId };
	}

	columnRenameIntent(
		tableId: string,
		oldColumnName: string,
		newHeader: string,
		newColumnName: string,
		intentId: string = crypto.randomUUID()
	): ColumnRenameActionIntent {
		//
		// Get table.
		//

		const table = this.latestState.tables.get(tableId);
		if (!table) {
			throw new Error(`Table ${tableId} not found`);
		}

		//
		// Remove and re-add every row with the renamed column.
		//

		let delta = ZSet.empty<unknown>();
		for (const row of table) {
			let newTuple: Tuple<unknown>;
			try {
				// First remove the old column
				const oldColumnPatchPath = `/${oldColumnName}`;
				const oldColumnAccessorKey: JsonPathLiteral = `$.${oldColumnName}`;
				const newColumnAccessorKey: JsonPathLiteral = `$.${newColumnName}`;
				const value = row.tuple.getValue();
				const oldValue = pluck(value, oldColumnAccessorKey);

				// Create new tuple with the column renamed
				newTuple = row.tuple
					.applyPatch([{ op: "remove", path: oldColumnPatchPath }])
					.addOrReplace(newColumnAccessorKey, oldValue);
			} catch {
				// Skip the record if there is no column to rename
				continue;
			}

			// Remove current version of the row
			delta = delta.add({ ...row, weight: -1 });

			// Add new version of the row with the renamed column
			delta = delta.add({ ...row, weight: 1, tuple: newTuple });
		}

		//
		// Update the query results.
		//

		const { queryResults, queryStates, changesByQueryId } = this.updateQueryResults(
			tableId,
			delta
		);

		//
		// Construct the return value.
		//

		const action: ColumnRenameAction = {
			type: "columnRename",
			intentId,
			table: tableId,
			desiredApplyEpoch: this.latestEpoch,
			oldColumnName,
			newHeader,
			newColumnName,
		};

		const proposedState: QueryEvaluatorState = {
			currentEpoch: this.latestEpoch + 1,
			tables: this.latestState.tables.set(tableId, table.merge(delta)),
			querySpecs: this.latestState.querySpecs,
			queryResults,
			queryStates,
			actionLog: this.latestState.actionLog.push(action),
			error: this.latestState.error,
		};

		return { action, proposedState, changesByQueryId };
	}

	columnTypeChangeIntent(
		tableId: string,
		columnName: string,
		oldTypeInfo: any,
		newTypeInfo: any,
		intentId: string = crypto.randomUUID()
	): ColumnTypeChangeActionIntent {
		//
		// Get table.
		//

		const table = this.latestState.tables.get(tableId);
		if (!table) {
			throw new Error(`Table ${tableId} not found`);
		}

		//
		// Check if this is a change to/from Reaction type.
		//

		const isChangingToReaction =
			newTypeInfo?.enrichedType === "Reaction" && oldTypeInfo?.enrichedType !== "Reaction";

		const isChangingFromReaction =
			oldTypeInfo?.enrichedType === "Reaction" && newTypeInfo?.enrichedType !== "Reaction";

		//
		// Create the reaction table name.
		//

		const reactionTableName = makeReactionTableName(tableId, columnName);

		//
		// Handle the data migration based on direction.
		//

		let originalTableDelta = ZSet.empty<unknown>();
		let reactionTableDelta = ZSet.empty<unknown>();

		if (isChangingToReaction) {
			//
			// Remove the column from the original table and create a new reaction table.
			//

			for (const row of table) {
				try {
					const columnAccessorKey: JsonPathLiteral = `$.${columnName}`;
					const value = row.tuple.getValue();
					const columnValue = pluck(value, columnAccessorKey);

					// Remove the column from the original table
					const path = `/${columnName}`;
					const newTuple = row.tuple.applyPatch([{ op: "remove", path }]);
					originalTableDelta = originalTableDelta.add({ ...row, weight: -1 });
					originalTableDelta = originalTableDelta.add({
						...row,
						weight: 1,
						tuple: newTuple,
					});
				} catch (error) {
					// Skip the record if there is no column to remove
					continue;
				}
			}
		} else if (isChangingFromReaction) {
			//
			// Add empty string to the original table and remove reaction table.
			//

			// Add empty string to all rows in the original table
			for (const row of table) {
				const accessorKey: JsonPathLiteral = `$.${columnName}`;
				const newTuple = row.tuple.addOrReplace(accessorKey, "");
				originalTableDelta = originalTableDelta.add({ ...row, weight: -1 });
				originalTableDelta = originalTableDelta.add({ ...row, weight: 1, tuple: newTuple });
			}

			// Remove all reactions from the reaction table
			const reactionTable = this.latestState.tables.get(reactionTableName);
			if (reactionTable) {
				for (const reactionRow of reactionTable) {
					reactionTableDelta = reactionTableDelta.add({ ...reactionRow, weight: -1 });
				}
			}
		}

		//
		// Update the query results for both tables.
		//

		const {
			queryResults: originalQueryResults,
			queryStates: originalQueryStates,
			changesByQueryId: originalChangesByQueryId,
		} = this.updateQueryResults(tableId, originalTableDelta);

		const {
			queryResults: reactionQueryResults,
			queryStates: reactionQueryStates,
			changesByQueryId: reactionChangesByQueryId,
		} = this.updateQueryResults(reactionTableName, reactionTableDelta);

		//
		// Merge the query results and changes.
		//

		let mergedQueryResults = originalQueryResults;
		let mergedQueryStates = originalQueryStates;
		let mergedChangesByQueryId = originalChangesByQueryId;

		for (const [queryId, delta] of reactionQueryResults) {
			const currentResults = mergedQueryResults.get(queryId) ?? ZSet.empty();
			mergedQueryResults = mergedQueryResults.set(queryId, currentResults.merge(delta));
		}

		for (const [queryId, states] of reactionQueryStates) {
			mergedQueryStates = mergedQueryStates.set(queryId, states);
		}

		for (const [queryId, changes] of reactionChangesByQueryId) {
			const existingChanges = mergedChangesByQueryId.get(queryId) ?? List();
			mergedChangesByQueryId = mergedChangesByQueryId.set(
				queryId,
				existingChanges.concat(changes)
			);
		}

		//
		// Update tables state.
		//

		const updatedTables = this.latestState.tables
			.set(tableId, table.merge(originalTableDelta))
			.set(reactionTableName, reactionTableDelta);

		//
		// Construct the return value.
		//

		const action: ColumnTypeChangeAction = {
			type: "columnTypeChange",
			intentId,
			table: tableId,
			desiredApplyEpoch: this.latestEpoch,
			columnName,
			oldTypeInfo,
			newTypeInfo,
		};

		const proposedState: QueryEvaluatorState = {
			currentEpoch: this.lastActionEpoch(),
			tables: updatedTables,
			querySpecs: this.latestState.querySpecs,
			queryResults: mergedQueryResults,
			queryStates: mergedQueryStates,
			actionLog: this.latestState.actionLog.push(action),
			error: this.latestState.error,
		};

		return { action, proposedState, changesByQueryId: mergedChangesByQueryId };
	}

	ensureReconciled(
		// Pass an entire QueryEvaluatorState in order to directly use it as the _committedState.
		// Otherwise pass just the actionList, and it will be used to incrementally reset the
		// _committedState before replaying.
		sourceOfTruth: QueryEvaluatorState | List<Action>,
		desiredHighWaterMark?: MutationActionIntent
	): { changesByQueryId: Map<string, List<TableMutation<unknown>>> } {
		// Step 1: Generate the reconciliation plan. This logic should not mutate the QueryEvaluator state.

		const overrideToSourceOfTruth = "actionLog" in sourceOfTruth;

		// Filter out actions from the source of truth that are already in the committed state.
		// The source of truth will "notify" us about actions that were already committed locally,
		// and those will no longer be present in the _actionIntents list.
		const sourceCommitted = overrideToSourceOfTruth ? sourceOfTruth.actionLog : sourceOfTruth;
		const locallyCommitted = this._committedState.actionLog
			.map((action) => action.intentId)
			.toSet();
		const sourceOfTruthActions = sourceCommitted.filter(
			(action) => !locallyCommitted.has(action.intentId)
		);

		const initialPendingActions = this._actionIntents;
		const plan = reconciliationPlan(
			sourceOfTruthActions,
			initialPendingActions,
			desiredHighWaterMark
		);

		// Step 2: Reset the current state to the source of truth
		let allChangesByQueryId = Map<string, List<TableMutation<unknown>>>();

		// 2z: If requested, override the _currentState entirely. This is special case
		//     logic primarily for handling initial table data.
		if (overrideToSourceOfTruth) {
			this._committedState = sourceOfTruth;
		} else {
			// 2a: Remove any _actionIntents that registered with conflicts.
			//     The plan gives us this by giving us _actionIntents (pendingActions)
			//     that were in the source of truth (committedActions) as knownActions.
			const intentsMinusRollbacks = plan.knownActions();

			// 2b: Regenerate the _actionIntents list to update their proposedStates.
			//     Notice that have to rebuild the _actionIntents list as we go, instead of
			//     immutably and overwriting at the end. This is because the last element of
			//     the _actionIntents list is used to calculate the next one.
			this._actionIntents = List();
			intentsMinusRollbacks.forEach(({ intent: { action } }) => {
				const replayedIntent = this.intentForAction(action);
				this._actionIntents = this._actionIntents.push(replayedIntent);
				if (replayedIntent.changesByQueryId) {
					allChangesByQueryId = allChangesByQueryId.mergeWith(
						(oldVal, newVal) => oldVal.merge(newVal),
						replayedIntent.changesByQueryId
					);
				}
			});

			// 2c: Add the server-provided apply actions on top, also generating their proposedStates.
			//     Same rule as above about needing to build _actionIntents as we go.
			const committedActions = plan.committedActions();
			committedActions.forEach(({ action }) => {
				const appliedIntent = this.intentForAction(action);
				this._actionIntents = this._actionIntents.push(appliedIntent);
				if (appliedIntent.changesByQueryId) {
					allChangesByQueryId = allChangesByQueryId.mergeWith(
						(oldVal, newVal) => oldVal.merge(newVal),
						appliedIntent.changesByQueryId
					);
				}
			});

			// 2d: Capture what should now be equivalent to the sourceOfTruthState
			//     We've now replayed all of the actions needed to reconstruct the source of truth,
			//     so now we can save it as the new _committedState and reset _actionIntents again.
			this._committedState = this.latestState;
		}
		this._actionIntents = List();
		this._topoSortedQueries = topologicallySortQueries(this._committedState.querySpecs);

		// Step 3: Rebuild any pending actions, included retries of rolled back changes

		const pendingActions = plan.allPendingActions();
		pendingActions.forEach(({ intent }) => {
			const replayedIntent = this.intentForAction(intent.action);
			this._actionIntents = this._actionIntents.push(replayedIntent);
			if (replayedIntent.changesByQueryId) {
				allChangesByQueryId = allChangesByQueryId.mergeWith(
					(oldVal, newVal) => oldVal.merge(newVal),
					replayedIntent.changesByQueryId
				);
			}
		});

		return {
			changesByQueryId: allChangesByQueryId,
		};
	}

	private intentForAction(action: Action): ActionIntent {
		if (action.type === "queryRegistration") {
			const { querySpec, intentId } = action;
			return this.queryIntent(querySpec, intentId);
		} else if (action.type === "mutation") {
			const { table, mutation, intentId } = action;
			return this.mutationIntent(table, this.latestEpoch, mutation, intentId);
		} else if (action.type === "columnAppend") {
			const { table, sqliteColumnDef, viewColumnDef, intentId } = action;
			return this.columnAppendIntent(table, sqliteColumnDef, viewColumnDef, intentId);
		} else if (action.type === "columnDelete") {
			const { table, columnName, intentId } = action;
			return this.columnDeleteIntent(table, columnName, intentId);
		} else if (action.type === "columnRename") {
			const { table, oldColumnName, newHeader, newColumnName, intentId } = action;
			return this.columnRenameIntent(
				table,
				oldColumnName,
				newHeader,
				newColumnName,
				intentId
			);
		} else if (action.type === "columnTypeChange") {
			const { table, columnName, oldTypeInfo, newTypeInfo, intentId } = action;
			return this.columnTypeChangeIntent(
				table,
				columnName,
				oldTypeInfo,
				newTypeInfo,
				intentId
			);
		} else {
			throw new Error("unknown intent type, cannot reconcile");
		}
	}

	private updateQueryResults(tableId: string, delta: ZSet<unknown>) {
		let currQueryDelta = Map<string, ZSet<unknown>>();
		let currQueryStates = this.latestState.queryStates;

		const tableDeltas = this.latestState.tables
			.map(() => ZSet.empty<unknown>())
			.set(tableId, delta);

		let updatedQueryIds = Set<string>();
		let changesByQueryId = Map<string, List<TableMutation<unknown>>>();
		for (const query of this._topoSortedQueries) {
			const { result, newStates } = query.update(
				tableDeltas,
				currQueryDelta,
				currQueryStates.get(query.queryKey) ?? Map()
			);
			currQueryDelta = currQueryDelta.set(query.queryKey, result);
			currQueryStates = currQueryStates.set(query.queryKey, newStates);
			if (result.size > 0) {
				updatedQueryIds = updatedQueryIds.add(query.queryKey);
			}

			let changes = List<TableMutation<unknown>>();
			for (const { tuple, weight } of result) {
				if (weight > 0) {
					changes = changes.push(insert(tuple));
				} else if (weight < 0) {
					changes = changes.push(deleteChange(tuple.id));
				}
			}
			changesByQueryId = changesByQueryId.set(query.queryKey, changes);
		}

		let queryResults = this.latestState.queryResults;

		for (const [queryId, delta] of currQueryDelta) {
			const currentResults = queryResults.get(queryId) ?? ZSet.empty();
			queryResults = queryResults.set(queryId, currentResults.merge(delta));
		}

		return { queryResults, queryStates: currQueryStates, changesByQueryId };
	}

	private lastActionEpoch(): number {
		const lastAction = this._actionIntents.last();
		const lastActionEpoch =
			isMutationActionIntent(lastAction) ||
			isColumnAppendActionIntent(lastAction) ||
			isColumnDeleteActionIntent(lastAction) ||
			isColumnRenameActionIntent(lastAction) ||
			isColumnTypeChangeActionIntent(lastAction)
				? lastAction.action.desiredApplyEpoch
				: lastAction?.action.desiredStartEpoch;
		return lastActionEpoch ?? this._committedState.currentEpoch;
	}
}
